import { cn } from "@/lib/utils";

/**
 * Manzana mordida — identidad de MANZANA MORDIDA ARG.
 *
 * Es el logo REAL: la mordida forma un perfil de cara. Lo que habia antes era
 * una manzana generica con hoja dibujada a mano en SVG, que no se parecia a la
 * marca; sobrevivia porque nadie habia comparado los dos al lado.
 *
 * Se dibuja como MASCARA CSS sobre `bg-current`, no como <img>. La silueta vive
 * en el canal alfa del PNG y el color lo pone el que la usa, asi que las clases
 * `text-foreground` / `text-primary` que le pasan sus consumidores siguen
 * mandando igual que cuando era un SVG con `fill="currentColor"` — y el logo se
 * invierte solo entre tema claro y oscuro sin una segunda copia del archivo.
 */
const MASCARA = {
  WebkitMaskImage: "url(/logo-manzana.png)",
  maskImage: "url(/logo-manzana.png)",
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",

  /*
   * EL TALLO ES UN ACENTO.
   *
   * Al recorte del PNG entra el tallo, que ocupa el 23,7% de arriba. Centrar
   * esa caja contra un texto deja el CUERPO de la manzana 11,9% mas abajo que
   * el centro optico —medido sobre el archivo— y el bloque se ve mal apoyado.
   *
   * Es el mismo problema que alinear por la tilde de la A en vez de por la
   * altura de mayuscula: al acento no se lo alinea, se lo deja sobresalir. Se
   * sube la marca ese 11%, que al ser relativo a su propia altura vale para
   * cualquier tamano. `transform` no toca el layout: mueve solo lo que se ve.
   */
  transform: "translateY(-11%)",
} as const;

export function LogoManzana({ className }: { className?: string }): JSX.Element {
  return (
    <span
      role="img"
      aria-label="MANZANA MORDIDA"
      className={cn("inline-block h-6 w-6 shrink-0 bg-current", className)}
      style={MASCARA}
    />
  );
}

/** La silueta sola, sin el ajuste del tallo: el cavado no se alinea con texto. */
const SILUETA = {
  WebkitMaskImage: MASCARA.WebkitMaskImage,
  maskImage: MASCARA.maskImage,
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
} as const;

/**
 * La manzana hundida en la bandeja gris del ticket vacio, como grabada.
 *
 * No es una imagen mas: es la misma silueta apilada tres veces con los colores
 * `--cavado-*` del tema. Abajo una luz corrida un pixel (el borde inferior que
 * agarra luz), encima el hueco en el color de la sombra, y adentro del hueco el
 * piso corrido hacia abajo y apenas desenfocado: lo que queda del hueco a la
 * vista es la sombra del borde de arriba. Se invierte sola entre claro y oscuro.
 *
 * Es decoracion: `aria-hidden`, y el tamaño lo pone el que la usa.
 */
export function LogoCavado({ className }: { className?: string }): JSX.Element {
  return (
    <span aria-hidden="true" className={cn("relative block aspect-square", className)}>
      <span
        className="absolute inset-0 translate-y-px"
        style={{ ...SILUETA, backgroundColor: "hsl(var(--cavado-luz))" }}
      />
      <span
        className="absolute inset-0"
        style={{ ...SILUETA, backgroundColor: "hsl(var(--cavado-sombra))" }}
      >
        <span
          className="absolute inset-0 translate-y-[1.5px] blur-[0.7px]"
          style={{ ...SILUETA, backgroundColor: "hsl(var(--cavado-piso))" }}
        />
      </span>
    </span>
  );
}

export function Logo({ className }: { className?: string }): JSX.Element {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/*
        24px, no 28. A 28 la manzana medía el doble de la altura de mayúscula
        del nombre y le competía; en un sidebar el símbolo acompaña a la marca
        escrita, no al revés.
      */}
      <LogoManzana className="h-6 w-6 shrink-0 text-foreground" />
      {/*
        El nombre, UNA vez.

        Habia dos renglones —"MANZANA MORDIDA" y debajo "Manzana Mordida"— que
        con el `uppercase` del segundo se leian identicos: el mismo nombre
        repetido, que parecia un error de copiado porque lo era. La bajada de la
        marca ("Donde tu Apple cobra vida") ya vive al pie del sidebar.
      */}
      {/*
        `tracking-wider`, no `tracking-tight`. El tracking negativo sirve para
        titulos en caja baja; sobre MAYUSCULAS hace lo contrario de lo que se
        busca, porque las mayusculas no tienen ascendentes ni descendentes que
        separen una letra de la otra y necesitan ese aire de mas.
      */}
      <div className="min-w-0 truncate text-sm font-semibold tracking-wider">MANZANA MORDIDA</div>
    </div>
  );
}
